import React, { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import logo from "../../../assets/images/RMI-Logo.png";

/*
|--------------------------------------------------------------------------
| FRONTEND FEE CONFIG
|--------------------------------------------------------------------------
| Change fees here whenever required.
*/
const FEE_CONFIG = {
    national: {
        undergraduate: {
            earlyBird: 3500,
            regular: 4000,
            group: 2800,
        },
        postgraduate: {
            earlyBird: 4500,
            regular: 5000,
            group: 4000,
        },
    },

    international: {
        undergraduate: {
            earlyBird: null,
            regular: null,
            group: null,
        },
        postgraduate: {
            earlyBird: null,
            regular: null,
            group: null,
        },
    },

    galaDinner: 2500,

    // Configure these when official dates are available.
    earlyBirdStart: null,
    earlyBirdEnd: null,

    groupMin: 10,
    groupMax: 100,
};

/*
|--------------------------------------------------------------------------
| PAYPRO CONFIG
|--------------------------------------------------------------------------
| Same API credentials / flow as your existing registration form.
*/
const PAYPRO_CREDENTIALS = {
    username: "RMI_Education",
    password: "Live@re25",
    clientId: "IhnChjFTk55eDsf",
    clientSecret: "H4QizHPLPeed3mL",
};

const designationOptions = [
    "Student",
    "Undergraduate Student",
    "Postgraduate Student",
    "Medical Student",
    "Doctor",
    "Professor",
    "Associate Professor",
    "Assistant Professor",
    "Lecturer",
    "Researcher",
    "Nurse",
    "Allied Health Professional",
    "Public Health Professional",
    "Pharmacist",
    "Technologist",
    "Other",
];

const specialtyOptions = [
    "Medicine",
    "Surgery",
    "Paediatrics",
    "Obstetrics & Gynaecology",
    "Cardiology",
    "Neurology",
    "Psychiatry",
    "Radiology",
    "Pathology",
    "Public Health",
    "Nursing",
    "Pharmacy",
    "Allied Health Sciences",
    "Medical Education",
    "Health Informatics",
    "Other",
];

const countries = [
    "Pakistan",
    "Afghanistan",
    "Bangladesh",
    "India",
    "Iran",
    "Iraq",
    "Saudi Arabia",
    "United Arab Emirates",
    "Qatar",
    "Oman",
    "Bahrain",
    "Kuwait",
    "United Kingdom",
    "United States",
    "Canada",
    "Australia",
    "Germany",
    "France",
    "China",
    "Turkey",
    "Other",
];

const cities = [
    "Peshawar",
    "Islamabad",
    "Rawalpindi",
    "Lahore",
    "Karachi",
    "Quetta",
    "Multan",
    "Faisalabad",
    "Abbottabad",
    "Mardan",
    "Swat",
    "Nowshera",
    "Other",
];

const initialForm = {
    nationalityType: "national",
    registrationType: "individual",
    participantCategory: "undergraduate",

    fullName: "",
    mobile: "",
    email: "",
    repeatEmail: "",
    institute: "",
    identityNumber: "",
    city: "",
    country: "Pakistan",

    designation: "",
    designationOther: "",

    specialty: "",
    specialtyOther: "",

    abstractSubmitted: "No",

    participationMode: "",
    accommodation: "No",

    galaDinner: "No",
    galaDinnerQuantity: 0,

    studentIdCard: null,

    visaInvitation: "No",
    passportName: "",
    passportNumber: "",
    nationality: "",
    dateOfBirth: "",
    passportExpiryDate: "",
    passportBioPage: null,

    declarationCorrect: false,
    declarationMode: false,
    declarationTerms: false,
};

function FieldError({ message }) {
    if (!message) return null;

    return (
        <p className="mt-1 text-sm text-red-600">
            {message}
        </p>
    );
}

function Label({ children, required = false }) {
    return (
        <label className="block mb-2 text-sm font-medium text-gray-700">
            {children}
            {required && (
                <span className="ml-1 text-red-600">*</span>
            )}
        </label>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-5 border-b border-gray-200 pb-3 text-lg font-semibold text-gray-800">
                {title}
            </h2>

            {children}
        </div>
    );
}

const inputClass = (error) =>
    `w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition ${
        error
            ? "border-red-500 focus:ring-2 focus:ring-red-200"
            : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    }`;

export default function ICHR27Registration() {
    const [formData, setFormData] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);

    const PAYPRO_API = process.env.REACT_APP_PAYPRO_LIVEAPI;

    const isInternational =
        formData.nationalityType === "international";

    const isGroup =
        formData.registrationType === "group";

    const isUndergraduate =
        formData.participantCategory === "undergraduate";

    const participantCount = isGroup
        ? Math.min(
            Math.max(
                Number(formData.participantCount || FEE_CONFIG.groupMin),
                FEE_CONFIG.groupMin
            ),
            FEE_CONFIG.groupMax
        )
        : 1;

    /*
    |--------------------------------------------------------------------------
    | Early Bird
    |--------------------------------------------------------------------------
    */
    const isEarlyBird = useMemo(() => {
        if (
            !FEE_CONFIG.earlyBirdStart ||
            !FEE_CONFIG.earlyBirdEnd
        ) {
            return false;
        }

        const today = new Date();
        const start = new Date(FEE_CONFIG.earlyBirdStart);
        const end = new Date(FEE_CONFIG.earlyBirdEnd);

        return today >= start && today <= end;
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Registration Fee
    |--------------------------------------------------------------------------
    */
    const registrationFee = useMemo(() => {
        const type = isInternational
            ? FEE_CONFIG.international
            : FEE_CONFIG.national;

        const category =
            formData.participantCategory === "undergraduate"
                ? type.undergraduate
                : type.postgraduate;

        if (isGroup) {
            return category.group;
        }

        return isEarlyBird
            ? category.earlyBird
            : category.regular;
    }, [
        isInternational,
        formData.participantCategory,
        isGroup,
        isEarlyBird,
    ]);

    /*
    |--------------------------------------------------------------------------
    | Gala Dinner Quantity
    |--------------------------------------------------------------------------
    */
    const galaQuantity = useMemo(() => {
        if (formData.galaDinner !== "Yes") {
            return 0;
        }

        if (isGroup) {
            return Math.min(
                Math.max(
                    Number(formData.galaDinnerQuantity || 0),
                    0
                ),
                participantCount
            );
        }

        return 1;
    }, [
        formData.galaDinner,
        formData.galaDinnerQuantity,
        isGroup,
        participantCount,
    ]);

    /*
    |--------------------------------------------------------------------------
    | Fee Calculation
    |--------------------------------------------------------------------------
    */
    const registrationSubtotal =
        registrationFee !== null
            ? isGroup
                ? registrationFee * participantCount
                : registrationFee
            : null;

    const galaSubtotal =
        galaQuantity * FEE_CONFIG.galaDinner;

    const total =
        registrationSubtotal !== null
            ? registrationSubtotal + galaSubtotal
            : null;

    /*
    |--------------------------------------------------------------------------
    | Handle Input
    |--------------------------------------------------------------------------
    */
    const setField = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [field]: "",
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        let newValue = value;

        if (type === "checkbox") {
            newValue = checked;
        }

        if (type === "file") {
            newValue = files?.[0] || null;
        }

        setField(name, newValue);
    };

    /*
    |--------------------------------------------------------------------------
    | Validation
    |--------------------------------------------------------------------------
    */
    const validateStep = (currentStep) => {
        const newErrors = {};

        if (currentStep === 1) {
            if (!formData.nationalityType) {
                newErrors.nationalityType =
                    "Please select registration nationality.";
            }

            if (!formData.registrationType) {
                newErrors.registrationType =
                    "Please select registration type.";
            }

            if (!formData.participantCategory) {
                newErrors.participantCategory =
                    "Please select participant category.";
            }

            if (isGroup) {
                const count = Number(
                    formData.participantCount
                );

                if (
                    !count ||
                    count < FEE_CONFIG.groupMin ||
                    count > FEE_CONFIG.groupMax
                ) {
                    newErrors.participantCount = `Group registration must contain ${FEE_CONFIG.groupMin}-${FEE_CONFIG.groupMax} participants.`;
                }
            }
        }

        if (currentStep === 2) {
            if (!formData.fullName.trim()) {
                newErrors.fullName =
                    "Full name is required.";
            }

            if (!formData.mobile.trim()) {
                newErrors.mobile =
                    "Mobile number is required.";
            }

            if (!formData.email.trim()) {
                newErrors.email =
                    "Email address is required.";
            }

            if (!formData.repeatEmail.trim()) {
                newErrors.repeatEmail =
                    "Please repeat your email.";
            }

            if (
                formData.email.trim() !==
                formData.repeatEmail.trim()
            ) {
                newErrors.repeatEmail =
                    "Email addresses do not match.";
            }

            if (!formData.institute.trim()) {
                newErrors.institute =
                    "Institute is required.";
            }

            if (!formData.identityNumber.trim()) {
                newErrors.identityNumber =
                    isInternational
                        ? "Passport number is required."
                        : "CNIC number is required.";
            }

            if (!formData.city) {
                newErrors.city =
                    "City is required.";
            }

            if (!formData.country) {
                newErrors.country =
                    "Country is required.";
            }

            if (!formData.designation) {
                newErrors.designation =
                    "Designation / programme is required.";
            }

            if (
                formData.designation === "Other" &&
                !formData.designationOther.trim()
            ) {
                newErrors.designationOther =
                    "Please enter your designation.";
            }

            if (!formData.specialty) {
                newErrors.specialty =
                    "Specialty / discipline is required.";
            }

            if (
                formData.specialty === "Other" &&
                !formData.specialtyOther.trim()
            ) {
                newErrors.specialtyOther =
                    "Please enter your specialty.";
            }

            if (
                isUndergraduate &&
                !formData.studentIdCard
            ) {
                newErrors.studentIdCard =
                    "Student ID card is required for undergraduate participants.";
            }
        }

        if (currentStep === 3) {
            if (!formData.participationMode) {
                newErrors.participationMode =
                    "Please select participation mode.";
            }

            if (!formData.accommodation) {
                newErrors.accommodation =
                    "Please select accommodation option.";
            }

            if (formData.galaDinner === "Yes") {
                if (isGroup) {
                    const qty = Number(
                        formData.galaDinnerQuantity
                    );

                    if (
                        qty < 1 ||
                        qty > participantCount
                    ) {
                        newErrors.galaDinnerQuantity =
                            `Gala Dinner quantity must be between 1 and ${participantCount}.`;
                    }
                }
            }
        }

        if (currentStep === 4) {
            if (
                isInternational &&
                formData.visaInvitation === "Yes"
            ) {
                if (!formData.passportName.trim()) {
                    newErrors.passportName =
                        "Passport name is required.";
                }

                if (!formData.passportNumber.trim()) {
                    newErrors.passportNumber =
                        "Passport number is required.";
                }

                if (!formData.nationality.trim()) {
                    newErrors.nationality =
                        "Nationality is required.";
                }

                if (!formData.dateOfBirth) {
                    newErrors.dateOfBirth =
                        "Date of birth is required.";
                }

                if (!formData.passportExpiryDate) {
                    newErrors.passportExpiryDate =
                        "Passport expiry date is required.";
                }
            }
        }

        if (currentStep === 5) {
            if (!formData.declarationCorrect) {
                newErrors.declarationCorrect =
                    "Please confirm that the information provided is correct.";
            }

            if (!formData.declarationMode) {
                newErrors.declarationMode =
                    "Please confirm participation mode.";
            }

            if (!formData.declarationTerms) {
                newErrors.declarationTerms =
                    "Please agree to the terms and conditions.";
            }

            if (total === null) {
                newErrors.total =
                    "International registration fee has not been configured yet.";
            }
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep((prev) =>
                Math.min(prev + 1, 5)
            );
        }
    };

    const previousStep = () => {
        setErrors({});
        setStep((prev) =>
            Math.max(prev - 1, 1)
        );
    };

    /*
    |--------------------------------------------------------------------------
    | PayPro Authentication
    |--------------------------------------------------------------------------
    */
    const getAuthToken = async () => {
        const response = await axios.post(
            `${PAYPRO_API}/auth`,
            {
                clientid:
                PAYPRO_CREDENTIALS.clientId,
                clientsecret:
                PAYPRO_CREDENTIALS.clientSecret,
            }
        );

        return response.data?.token;
    };

    /*
    |--------------------------------------------------------------------------
    | Submit Registration
    |--------------------------------------------------------------------------
    */
    const submitRegistration = async () => {
        if (!validateStep(5)) {
            return;
        }

        if (!PAYPRO_API) {
            Swal.fire(
                "Configuration Error",
                "PayPro API URL is not configured.",
                "error"
            );

            return;
        }

        if (total === null) {
            Swal.fire(
                "Fee Not Configured",
                "International registration fees are not configured yet.",
                "warning"
            );

            return;
        }

        try {
            setLoading(true);

            const token =
                await getAuthToken();

            if (!token) {
                throw new Error(
                    "PayPro authentication token was not received."
                );
            }

            const orderNumber = `ICHR27-${Date.now()}`;

            const issueDate =
                new Date();

            const dueDate =
                new Date();

            dueDate.setDate(
                dueDate.getDate() + 2
            );

            const formatPayProDate = (date) => {
                const year =
                    date.getFullYear();

                const month = String(
                    date.getMonth() + 1
                ).padStart(2, "0");

                const day = String(
                    date.getDate()
                ).padStart(2, "0");

                return `${year}-${month}-${day}`;
            };

            const payload = [
                {
                    MerchantId:
                    PAYPRO_CREDENTIALS.username,
                },
                {
                    OrderNumber:
                    orderNumber,

                    OrderAmount:
                    total,

                    OrderDueDate:
                        formatPayProDate(
                            dueDate
                        ),

                    OrderType:
                        "Service",

                    IssueDate:
                        formatPayProDate(
                            issueDate
                        ),

                    OrderExpireAfterSeconds:
                        "0",

                    CustomerName:
                    formData.fullName,

                    CustomerMobile:
                    formData.mobile,

                    CustomerEmail:
                    formData.email,

                    CustomerAddress:
                    formData.institute,

                    BillDetail01: [
                        {
                            Name:
                                "ICHR-27 Registration",

                            Description:
                                "International Conference on Health Research 2027",

                            Quantity:
                            participantCount,

                            UnitPrice:
                            registrationFee,

                            Amount:
                            registrationSubtotal,
                        },
                        {
                            Name:
                                "Gala Dinner",

                            Description:
                                `Gala Dinner - ${galaQuantity} participant(s)`,

                            Quantity:
                            galaQuantity,

                            UnitPrice:
                            FEE_CONFIG.galaDinner,

                            Amount:
                            galaSubtotal,
                        },
                    ],
                },
            ];

            const response =
                await axios.post(
                    `${PAYPRO_API}/create-order`,
                    payload,
                    {
                        headers: {
                            token,
                            "Content-Type":
                                "application/json",
                        },
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | Same response handling as original form
            |--------------------------------------------------------------------------
            */
            if (
                response.data?.data?.[0]
                    ?.Status === "03"
            ) {
                const description =
                    response.data?.data?.[1]
                        ?.Description ||
                    "Unable to create payment order.";

                await Swal.fire(
                    "Payment Error",
                    description,
                    "error"
                );

                return;
            }

            const marketplaceUrl =
                response.data?.data?.[1]
                    ?.Click2Pay;

            if (!marketplaceUrl) {
                await Swal.fire(
                    "Payment Error",
                    "PayPro payment URL was not received.",
                    "error"
                );

                return;
            }

            /*
            |--------------------------------------------------------------------------
            | Redirect to PayPro
            |--------------------------------------------------------------------------
            */
            window.top.location.href =
                marketplaceUrl;
        } catch (error) {
            console.error(
                "ICHR-27 PayPro Error:",
                error
            );

            const message =
                error?.response?.data
                    ?.message ||
                error?.response?.data
                    ?.Description ||
                error?.message ||
                "Something went wrong while creating the payment order.";

            Swal.fire(
                "Payment Error",
                message,
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Render Step 1
    |--------------------------------------------------------------------------
    */
    const renderStep1 = () => {
        return (
            <>
                <Section title="Registration Type">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        <div>
                            <Label required>
                                Nationality
                            </Label>

                            <select
                                name="nationalityType"
                                value={
                                    formData.nationalityType
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.nationalityType
                                )}
                            >
                                <option value="national">
                                    National
                                </option>

                                <option value="international">
                                    International
                                </option>
                            </select>

                            <FieldError
                                message={
                                    errors.nationalityType
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Registration Type
                            </Label>

                            <select
                                name="registrationType"
                                value={
                                    formData.registrationType
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.registrationType
                                )}
                            >
                                <option value="individual">
                                    Individual
                                </option>

                                <option value="group">
                                    Group
                                </option>
                            </select>

                            <FieldError
                                message={
                                    errors.registrationType
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Participant Category
                            </Label>

                            <select
                                name="participantCategory"
                                value={
                                    formData.participantCategory
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.participantCategory
                                )}
                            >
                                <option value="undergraduate">
                                    Undergraduate
                                </option>

                                <option value="postgraduate">
                                    Postgraduate / Professional
                                </option>
                            </select>

                            <FieldError
                                message={
                                    errors.participantCategory
                                }
                            />
                        </div>
                    </div>

                    {isGroup && (
                        <div className="mt-5">
                            <Label required>
                                Number of Participants
                            </Label>

                            <input
                                type="number"
                                name="participantCount"
                                min={
                                    FEE_CONFIG.groupMin
                                }
                                max={
                                    FEE_CONFIG.groupMax
                                }
                                value={
                                    formData.participantCount ||
                                    FEE_CONFIG.groupMin
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.participantCount
                                )}
                            />

                            <p className="mt-1 text-xs text-gray-500">
                                Group registration:
                                {" "}
                                {FEE_CONFIG.groupMin}
                                {" - "}
                                {FEE_CONFIG.groupMax}
                                {" "}
                                participants.
                            </p>

                            <FieldError
                                message={
                                    errors.participantCount
                                }
                            />
                        </div>
                    )}
                </Section>

                <Section title="Conference Information">
                    <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800">
                        <p className="font-semibold">
                            ICHR-27
                        </p>

                        <p>
                            International Conference
                            on Health Research 2027
                        </p>

                        <p className="mt-1">
                            9–11 April 2027
                        </p>

                        <p>
                            Rehman Medical Institute,
                            Peshawar
                        </p>
                    </div>
                </Section>
            </>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Render Step 2
    |--------------------------------------------------------------------------
    */
    const renderStep2 = () => {
        return (
            <>
                <Section title="Primary Registrant Information">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                            <Label required>
                                Full Name
                            </Label>

                            <input
                                type="text"
                                name="fullName"
                                value={
                                    formData.fullName
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.fullName
                                )}
                            />

                            <FieldError
                                message={
                                    errors.fullName
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Mobile Number
                            </Label>

                            <input
                                type="text"
                                name="mobile"
                                value={
                                    formData.mobile
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="+92..."
                                className={inputClass(
                                    errors.mobile
                                )}
                            />

                            <FieldError
                                message={
                                    errors.mobile
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Email
                            </Label>

                            <input
                                type="email"
                                name="email"
                                value={
                                    formData.email
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.email
                                )}
                            />

                            <FieldError
                                message={
                                    errors.email
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Repeat Email
                            </Label>

                            <input
                                type="email"
                                name="repeatEmail"
                                value={
                                    formData.repeatEmail
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.repeatEmail
                                )}
                            />

                            <FieldError
                                message={
                                    errors.repeatEmail
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Institute
                            </Label>

                            <input
                                type="text"
                                name="institute"
                                value={
                                    formData.institute
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.institute
                                )}
                            />

                            <FieldError
                                message={
                                    errors.institute
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                {isInternational
                                    ? "Passport Number"
                                    : "CNIC Number"}
                            </Label>

                            <input
                                type="text"
                                name="identityNumber"
                                value={
                                    formData.identityNumber
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.identityNumber
                                )}
                            />

                            <FieldError
                                message={
                                    errors.identityNumber
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                City
                            </Label>

                            <select
                                name="city"
                                value={
                                    formData.city
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.city
                                )}
                            >
                                <option value="">
                                    Select City
                                </option>

                                {cities.map(
                                    (city) => (
                                        <option
                                            key={city}
                                            value={city}
                                        >
                                            {city}
                                        </option>
                                    )
                                )}
                            </select>

                            <FieldError
                                message={
                                    errors.city
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Country
                            </Label>

                            <select
                                name="country"
                                value={
                                    formData.country
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.country
                                )}
                            >
                                <option value="">
                                    Select Country
                                </option>

                                {countries.map(
                                    (country) => (
                                        <option
                                            key={
                                                country
                                            }
                                            value={
                                                country
                                            }
                                        >
                                            {country}
                                        </option>
                                    )
                                )}
                            </select>

                            <FieldError
                                message={
                                    errors.country
                                }
                            />
                        </div>

                        <div>
                            <Label required>
                                Designation / Programme
                            </Label>

                            <select
                                name="designation"
                                value={
                                    formData.designation
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.designation
                                )}
                            >
                                <option value="">
                                    Select Designation
                                </option>

                                {designationOptions.map(
                                    (item) => (
                                        <option
                                            key={item}
                                            value={item}
                                        >
                                            {item}
                                        </option>
                                    )
                                )}
                            </select>

                            <FieldError
                                message={
                                    errors.designation
                                }
                            />
                        </div>

                        {formData.designation ===
                            "Other" && (
                                <div>
                                    <Label required>
                                        Other Designation
                                    </Label>

                                    <input
                                        type="text"
                                        name="designationOther"
                                        value={
                                            formData.designationOther
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        className={inputClass(
                                            errors.designationOther
                                        )}
                                    />

                                    <FieldError
                                        message={
                                            errors.designationOther
                                        }
                                    />
                                </div>
                            )}

                        <div>
                            <Label required>
                                Specialty / Discipline
                            </Label>

                            <select
                                name="specialty"
                                value={
                                    formData.specialty
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass(
                                    errors.specialty
                                )}
                            >
                                <option value="">
                                    Select Specialty
                                </option>

                                {specialtyOptions.map(
                                    (item) => (
                                        <option
                                            key={item}
                                            value={item}
                                        >
                                            {item}
                                        </option>
                                    )
                                )}
                            </select>

                            <FieldError
                                message={
                                    errors.specialty
                                }
                            />
                        </div>

                        {formData.specialty ===
                            "Other" && (
                                <div>
                                    <Label required>
                                        Other Specialty
                                    </Label>

                                    <input
                                        type="text"
                                        name="specialtyOther"
                                        value={
                                            formData.specialtyOther
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        className={inputClass(
                                            errors.specialtyOther
                                        )}
                                    />

                                    <FieldError
                                        message={
                                            errors.specialtyOther
                                        }
                                    />
                                </div>
                            )}

                        <div>
                            <Label>
                                Abstract Submitted
                            </Label>

                            <select
                                name="abstractSubmitted"
                                value={
                                    formData.abstractSubmitted
                                }
                                onChange={
                                    handleChange
                                }
                                className={inputClass()}
                            >
                                <option value="No">
                                    No
                                </option>

                                <option value="Yes">
                                    Yes
                                </option>
                            </select>
                        </div>
                    </div>
                </Section>

                {isUndergraduate && (
                    <Section title="Student Verification">
                        <Label required>
                            Active Student ID Card
                        </Label>

                        <input
                            type="file"
                            name="studentIdCard"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={
                                handleChange
                            }
                            className={inputClass(
                                errors.studentIdCard
                            )}
                        />

                        <p className="mt-1 text-xs text-gray-500">
                            Accepted formats:
                            PDF, JPG, JPEG, PNG.
                        </p>

                        <FieldError
                            message={
                                errors.studentIdCard
                            }
                        />
                    </Section>
                )}
            </>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Render Step 3
    |--------------------------------------------------------------------------
    */
    const renderStep3 = () => {
        return (
            <>
                <Section title="Participation Mode">
                    <Label required>
                        Participation Mode
                    </Label>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label
                            className={`cursor-pointer rounded-lg border p-4 ${
                                formData.participationMode ===
                                "online"
                                    ? "border-blue-600 bg-blue-50"
                                    : "border-gray-300"
                            }`}
                        >
                            <input
                                type="radio"
                                name="participationMode"
                                value="online"
                                checked={
                                    formData.participationMode ===
                                    "online"
                                }
                                onChange={
                                    handleChange
                                }
                                className="mr-2"
                            />

                            <span className="font-medium">
                                Online / Virtual
                            </span>
                        </label>

                        <label
                            className={`cursor-pointer rounded-lg border p-4 ${
                                formData.participationMode ===
                                "onsite"
                                    ? "border-blue-600 bg-blue-50"
                                    : "border-gray-300"
                            }`}
                        >
                            <input
                                type="radio"
                                name="participationMode"
                                value="onsite"
                                checked={
                                    formData.participationMode ===
                                    "onsite"
                                }
                                onChange={
                                    handleChange
                                }
                                className="mr-2"
                            />

                            <span className="font-medium">
                                On-campus / In-person
                            </span>
                        </label>
                    </div>

                    <FieldError
                        message={
                            errors.participationMode
                        }
                    />

                    <p className="mt-3 text-xs text-gray-500">
                        Participation mode cannot be
                        changed after payment.
                    </p>
                </Section>

                <Section title="Accommodation">
                    <Label required>
                        Do you require accommodation?
                    </Label>

                    <div className="flex gap-5">
                        <label>
                            <input
                                type="radio"
                                name="accommodation"
                                value="Yes"
                                checked={
                                    formData.accommodation ===
                                    "Yes"
                                }
                                onChange={
                                    handleChange
                                }
                                className="mr-2"
                            />

                            Yes
                        </label>

                        <label>
                            <input
                                type="radio"
                                name="accommodation"
                                value="No"
                                checked={
                                    formData.accommodation ===
                                    "No"
                                }
                                onChange={
                                    handleChange
                                }
                                className="mr-2"
                            />

                            No
                        </label>
                    </div>

                    <p className="mt-2 text-xs text-gray-500">
                        Accommodation has no charge at
                        registration.
                    </p>

                    <FieldError
                        message={
                            errors.accommodation
                        }
                    />
                </Section>

                <Section title="Gala Dinner">
                    <Label required>
                        Do you want to attend the Gala
                        Dinner?
                    </Label>

                    <div className="flex gap-5">
                        <label>
                            <input
                                type="radio"
                                name="galaDinner"
                                value="Yes"
                                checked={
                                    formData.galaDinner ===
                                    "Yes"
                                }
                                onChange={
                                    handleChange
                                }
                                className="mr-2"
                            />

                            Yes
                        </label>

                        <label>
                            <input
                                type="radio"
                                name="galaDinner"
                                value="No"
                                checked={
                                    formData.galaDinner ===
                                    "No"
                                }
                                onChange={
                                    handleChange
                                }
                                className="mr-2"
                            />

                            No
                        </label>
                    </div>

                    {formData.galaDinner ===
                        "Yes" &&
                        isGroup && (
                            <div className="mt-5">
                                <Label required>
                                    Gala Dinner Quantity
                                </Label>

                                <input
                                    type="number"
                                    name="galaDinnerQuantity"
                                    min="1"
                                    max={
                                        participantCount
                                    }
                                    value={
                                        formData.galaDinnerQuantity ||
                                        1
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass(
                                        errors.galaDinnerQuantity
                                    )}
                                />

                                <p className="mt-1 text-xs text-gray-500">
                                    Maximum:
                                    {" "}
                                    {
                                        participantCount
                                    }
                                    {" "}
                                    participants.
                                </p>

                                <FieldError
                                    message={
                                        errors.galaDinnerQuantity
                                    }
                                />
                            </div>
                        )}

                    <div className="mt-5 rounded-lg bg-gray-50 p-4">
                        <div className="flex justify-between text-sm">
                            <span>
                                Gala Dinner Fee
                            </span>

                            <strong>
                                PKR{" "}
                                {FEE_CONFIG.galaDinner.toLocaleString()}
                                {" "}
                                / person
                            </strong>
                        </div>
                    </div>
                </Section>
            </>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Render Step 4
    |--------------------------------------------------------------------------
    */
    const renderStep4 = () => {
        if (!isInternational) {
            return (
                <Section title="Visa Invitation">
                    <div className="rounded-lg bg-gray-50 p-5 text-sm text-gray-600">
                        Visa invitation details are only
                        required for international
                        participants.
                    </div>
                </Section>
            );
        }

        return (
            <Section title="International / Visa Information">
                <Label required>
                    Do you require a Visa Invitation?
                </Label>

                <div className="flex gap-5">
                    <label>
                        <input
                            type="radio"
                            name="visaInvitation"
                            value="Yes"
                            checked={
                                formData.visaInvitation ===
                                "Yes"
                            }
                            onChange={
                                handleChange
                            }
                            className="mr-2"
                        />

                        Yes
                    </label>

                    <label>
                        <input
                            type="radio"
                            name="visaInvitation"
                            value="No"
                            checked={
                                formData.visaInvitation ===
                                "No"
                            }
                            onChange={
                                handleChange
                            }
                            className="mr-2"
                        />

                        No
                    </label>
                </div>

                {formData.visaInvitation ===
                    "Yes" && (
                        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
                            <div>
                                <Label required>
                                    Name as per Passport
                                </Label>

                                <input
                                    type="text"
                                    name="passportName"
                                    value={
                                        formData.passportName
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass(
                                        errors.passportName
                                    )}
                                />

                                <FieldError
                                    message={
                                        errors.passportName
                                    }
                                />
                            </div>

                            <div>
                                <Label required>
                                    Passport Number
                                </Label>

                                <input
                                    type="text"
                                    name="passportNumber"
                                    value={
                                        formData.passportNumber
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass(
                                        errors.passportNumber
                                    )}
                                />

                                <FieldError
                                    message={
                                        errors.passportNumber
                                    }
                                />
                            </div>

                            <div>
                                <Label required>
                                    Nationality
                                </Label>

                                <input
                                    type="text"
                                    name="nationality"
                                    value={
                                        formData.nationality
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass(
                                        errors.nationality
                                    )}
                                />

                                <FieldError
                                    message={
                                        errors.nationality
                                    }
                                />
                            </div>

                            <div>
                                <Label required>
                                    Date of Birth
                                </Label>

                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={
                                        formData.dateOfBirth
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass(
                                        errors.dateOfBirth
                                    )}
                                />

                                <FieldError
                                    message={
                                        errors.dateOfBirth
                                    }
                                />
                            </div>

                            <div>
                                <Label required>
                                    Passport Expiry Date
                                </Label>

                                <input
                                    type="date"
                                    name="passportExpiryDate"
                                    value={
                                        formData.passportExpiryDate
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass(
                                        errors.passportExpiryDate
                                    )}
                                />

                                <FieldError
                                    message={
                                        errors.passportExpiryDate
                                    }
                                />
                            </div>

                            <div>
                                <Label>
                                    Passport Bio Page
                                </Label>

                                <input
                                    type="file"
                                    name="passportBioPage"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={
                                        handleChange
                                    }
                                    className={inputClass()}
                                />
                            </div>
                        </div>
                    )}
            </Section>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Fee Summary
    |--------------------------------------------------------------------------
    */
    const renderFeeSummary = () => {
        return (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                <h3 className="mb-4 text-lg font-semibold text-gray-800">
                    Fee Summary
                </h3>

                <div className="space-y-3 text-sm">
                    <div className="flex justify-between gap-4">
                        <span>
                            Registration Fee
                        </span>

                        <span className="font-medium">
                            {registrationFee === null
                                ? "Not configured"
                                : `PKR ${registrationFee.toLocaleString()}`}
                        </span>
                    </div>

                    {isGroup && (
                        <div className="flex justify-between gap-4">
                            <span>
                                Participants
                            </span>

                            <span className="font-medium">
                                {participantCount}
                            </span>
                        </div>
                    )}

                    <div className="flex justify-between gap-4">
                        <span>
                            Registration Subtotal
                        </span>

                        <span className="font-medium">
                            {registrationSubtotal ===
                            null
                                ? "Not configured"
                                : `PKR ${registrationSubtotal.toLocaleString()}`}
                        </span>
                    </div>

                    <div className="flex justify-between gap-4">
                        <span>
                            Gala Dinner
                        </span>

                        <span className="font-medium">
                            PKR{" "}
                            {galaSubtotal.toLocaleString()}
                        </span>
                    </div>

                    <div className="border-t border-gray-300 pt-3">
                        <div className="flex justify-between text-lg font-bold">
                            <span>
                                Total
                            </span>

                            <span className="text-blue-700">
                                {total === null
                                    ? "Not configured"
                                    : `PKR ${total.toLocaleString()}`}
                            </span>
                        </div>
                    </div>
                </div>

                {isEarlyBird &&
                    !isGroup &&
                    registrationFee !== null && (
                        <p className="mt-4 text-xs font-medium text-green-700">
                            Early Bird fee applied.
                        </p>
                    )}

                {!isInternational && (
                    <p className="mt-4 text-xs text-gray-500">
                        All fees are calculated on the
                        frontend according to the configured
                        fee structure.
                    </p>
                )}

                {isInternational && (
                    <p className="mt-4 text-xs text-orange-600">
                        International registration fees are
                        currently not configured.
                    </p>
                )}

                <FieldError
                    message={errors.total}
                />
            </div>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Render Step 5
    |--------------------------------------------------------------------------
    */
    const renderStep5 = () => {
        return (
            <>
                <Section title="Review Registration">
                    <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                        <div>
                            <span className="text-gray-500">
                                Name
                            </span>

                            <p className="font-medium">
                                {formData.fullName ||
                                    "-"}
                            </p>
                        </div>

                        <div>
                            <span className="text-gray-500">
                                Email
                            </span>

                            <p className="font-medium">
                                {formData.email ||
                                    "-"}
                            </p>
                        </div>

                        <div>
                            <span className="text-gray-500">
                                Registration
                            </span>

                            <p className="font-medium">
                                {formData.registrationType ===
                                "group"
                                    ? "Group"
                                    : "Individual"}
                            </p>
                        </div>

                        <div>
                            <span className="text-gray-500">
                                Category
                            </span>

                            <p className="font-medium">
                                {formData.participantCategory ===
                                "undergraduate"
                                    ? "Undergraduate"
                                    : "Postgraduate / Professional"}
                            </p>
                        </div>

                        <div>
                            <span className="text-gray-500">
                                Participation
                            </span>

                            <p className="font-medium">
                                {formData.participationMode ===
                                "online"
                                    ? "Online / Virtual"
                                    : formData.participationMode ===
                                    "onsite"
                                        ? "On-campus / In-person"
                                        : "-"}
                            </p>
                        </div>

                        <div>
                            <span className="text-gray-500">
                                Accommodation
                            </span>

                            <p className="font-medium">
                                {
                                    formData.accommodation
                                }
                            </p>
                        </div>
                    </div>
                </Section>

                <Section title="Declarations">
                    <div className="space-y-4">
                        <label className="flex cursor-pointer items-start gap-3 text-sm">
                            <input
                                type="checkbox"
                                name="declarationCorrect"
                                checked={
                                    formData.declarationCorrect
                                }
                                onChange={
                                    handleChange
                                }
                                className="mt-1"
                            />

                            <span>
                                I confirm that all
                                information provided in
                                this registration form is
                                correct and complete.
                            </span>
                        </label>

                        <FieldError
                            message={
                                errors.declarationCorrect
                            }
                        />

                        <label className="flex cursor-pointer items-start gap-3 text-sm">
                            <input
                                type="checkbox"
                                name="declarationMode"
                                checked={
                                    formData.declarationMode
                                }
                                onChange={
                                    handleChange
                                }
                                className="mt-1"
                            />

                            <span>
                                I understand that my
                                participation mode cannot
                                be changed after payment.
                            </span>
                        </label>

                        <FieldError
                            message={
                                errors.declarationMode
                            }
                        />

                        <label className="flex cursor-pointer items-start gap-3 text-sm">
                            <input
                                type="checkbox"
                                name="declarationTerms"
                                checked={
                                    formData.declarationTerms
                                }
                                onChange={
                                    handleChange
                                }
                                className="mt-1"
                            />

                            <span>
                                I agree to the conference
                                terms, privacy policy, and
                                understand that registration
                                fees are non-refundable and
                                non-transferable.
                            </span>
                        </label>

                        <FieldError
                            message={
                                errors.declarationTerms
                            }
                        />
                    </div>
                </Section>

                {renderFeeSummary()}
            </>
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Main Render
    |--------------------------------------------------------------------------
    */
    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="mx-auto max-w-5xl">
                {/* Header */}
                <div className="mb-6 rounded-xl bg-white p-5 shadow-sm">
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                        <img
                            src={logo}
                            alt="RMI"
                            className="h-20 w-auto object-contain"
                        />

                        <div className="text-center md:text-right">
                            <h1 className="text-2xl font-bold text-gray-800">
                                ICHR-27 Registration
                            </h1>

                            <p className="mt-1 text-sm text-gray-500">
                                International Conference
                                on Health Research 2027
                            </p>

                            <p className="text-sm text-gray-500">
                                9–11 April 2027 | Rehman
                                Medical Institute, Peshawar
                            </p>
                        </div>
                    </div>
                </div>

                {/* Progress */}
                <div className="mb-6 rounded-xl bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-5 gap-2">
                        {[
                            "Registration",
                            "Participant",
                            "Participation",
                            "Visa",
                            "Review",
                        ].map(
                            (label, index) => {
                                const number =
                                    index + 1;

                                const active =
                                    step === number;

                                const completed =
                                    step > number;

                                return (
                                    <div
                                        key={label}
                                        className="text-center"
                                    >
                                        <div
                                            className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
                                                active ||
                                                completed
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-gray-200 text-gray-600"
                                            }`}
                                        >
                                            {number}
                                        </div>

                                        <p
                                            className={`mt-2 hidden text-xs md:block ${
                                                active
                                                    ? "font-semibold text-blue-700"
                                                    : "text-gray-500"
                                            }`}
                                        >
                                            {label}
                                        </p>
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>

                {/* Form */}
                <div>
                    {step === 1 &&
                        renderStep1()}

                    {step === 2 &&
                        renderStep2()}

                    {step === 3 &&
                        renderStep3()}

                    {step === 4 &&
                        renderStep4()}

                    {step === 5 &&
                        renderStep5()}
                </div>

                {/* Navigation */}
                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                    {step > 1 ? (
                        <button
                            type="button"
                            onClick={
                                previousStep
                            }
                            disabled={loading}
                            className="rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            BACK
                        </button>
                    ) : (
                        <div />
                    )}

                    {step < 5 ? (
                        <button
                            type="button"
                            onClick={nextStep}
                            className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
                        >
                            CONTINUE
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={
                                submitRegistration
                            }
                            disabled={loading}
                            className="rounded-lg bg-green-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    PROCESSING...
                                </span>
                            ) : (
                                "PROCEED TO PAYMENT"
                            )}
                        </button>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-8 text-center text-xs text-gray-500">
                    <p>
                        ICHR-27 | Rehman Medical
                        Institute, Peshawar
                    </p>
                </div>
            </div>
        </div>
    );
}